import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatPhoneNumber } from '../../services/api';

interface RegisterFormProps {
  onSuccess: (phone: string) => void;
  onSwitchToLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const { register, loading } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    phone_number: '',
    sa_id_number: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    bank_account_number: '',
    bank_name: '',
    bank_branch_code: '',
    account_holder_name: '',
    acceptTerms: false
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Phone number validation - allow admin numbers
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required';
    } else {
      const phoneRegex = /^(\+27|0)[0-9]{9}$/;
      const adminRegex = /^.*admin.*$/i; // Allow any number containing "admin"
      
      if (!phoneRegex.test(formData.phone_number.trim()) && !adminRegex.test(formData.phone_number.trim())) {
        newErrors.phone_number = 'Please enter a valid South African phone number or admin credentials';
      }
    }

    // SA ID number validation
    if (!formData.sa_id_number.trim()) {
      newErrors.sa_id_number = 'South African ID number is required';
    } else if (formData.sa_id_number.length !== 13) {
      newErrors.sa_id_number = 'SA ID number must be 13 digits';
    } else if (!/^[0-9]{13}$/.test(formData.sa_id_number)) {
      newErrors.sa_id_number = 'SA ID number must contain only numbers';
    }

    // First name validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    } else if (formData.first_name.trim().length < 2) {
      newErrors.first_name = 'First name must be at least 2 characters';
    }

    // Last name validation
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    } else if (formData.last_name.trim().length < 2) {
      newErrors.last_name = 'Last name must be at least 2 characters';
    }

    // Email validation (optional)
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms acceptance
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep2()) return;

    const registrationData = {
      phone_number: formatPhoneNumber(formData.phone_number.trim()),
      sa_id_number: formData.sa_id_number.trim(),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email.trim() || undefined,
      password: formData.password,
      bank_account_number: formData.bank_account_number.trim() || undefined,
      bank_name: formData.bank_name.trim() || undefined,
      bank_branch_code: formData.bank_branch_code.trim() || undefined,
      account_holder_name: formData.account_holder_name.trim() || undefined,
    };

    const result = await register(registrationData);

    if (result.success) {
      onSuccess(formatPhoneNumber(formData.phone_number.trim()));
    } else {
      setErrors({ general: result.message });
    }
  };

  return (
    <div className="register-form">
      <div className="form-header">
        <h1>Join GenLend</h1>
        <p>Get quick access to microloans for NSFAS beneficiaries</p>
        <div className="step-indicator">
          <span className={`step ${currentStep >= 1 ? 'active' : ''}`}>1</span>
          <span className="step-divider"></span>
          <span className={`step ${currentStep >= 2 ? 'active' : ''}`}>2</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="auth-form">
        {errors.general && (
          <div className="error-message general-error">
            {errors.general}
          </div>
        )}

        {currentStep === 1 && (
          <div className="form-step">
            <h3>Personal Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name">First Name</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  className={errors.first_name ? 'error' : ''}
                />
                {errors.first_name && (
                  <span className="error-message">{errors.first_name}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="last_name">Last Name</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  className={errors.last_name ? 'error' : ''}
                />
                {errors.last_name && (
                  <span className="error-message">{errors.last_name}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone_number">Phone Number</label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                placeholder="0123456789"
                className={errors.phone_number ? 'error' : ''}
              />
              {errors.phone_number && (
                <span className="error-message">{errors.phone_number}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="sa_id_number">South African ID Number</label>
              <input
                type="text"
                id="sa_id_number"
                name="sa_id_number"
                value={formData.sa_id_number}
                onChange={handleInputChange}
                placeholder="1234567890123"
                maxLength={13}
                className={errors.sa_id_number ? 'error' : ''}
              />
              {errors.sa_id_number && (
                <span className="error-message">{errors.sa_id_number}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email (Optional)</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                className={errors.email ? 'error' : ''}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <button
              type="button"
              className="btn btn-primary btn-full"
              onClick={handleNext}
            >
              Next
            </button>
          </div>
        )}

        {currentStep === 2 && (
          <div className="form-step">
            <h3>Security & Banking</h3>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create a strong password"
                  className={errors.password ? 'error' : ''}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M21 4L3 20"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <span className="error-message">{errors.password}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your password"
                className={errors.confirmPassword ? 'error' : ''}
                disabled={loading}
              />
              {errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword}</span>
              )}
            </div>

            <div className="form-section">
              <h4>Banking Details (Optional)</h4>
              <p className="form-help">Add your banking details now or complete later</p>
              
              <div className="form-group">
                <label htmlFor="bank_name">Bank Name</label>
                <select
                  id="bank_name"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                >
                  <option value="">Select your bank</option>
                  <option value="Standard Bank">Standard Bank</option>
                  <option value="FNB">FNB</option>
                  <option value="Nedbank">Nedbank</option>
                  <option value="ABSA">ABSA</option>
                  <option value="Capitec">Capitec</option>
                  <option value="Discovery Bank">Discovery Bank</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="bank_account_number">Account Number</label>
                <input
                  type="text"
                  id="bank_account_number"
                  name="bank_account_number"
                  value={formData.bank_account_number}
                  onChange={handleInputChange}
                  placeholder="Your account number"
                />
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                  className={errors.acceptTerms ? 'error' : ''}
                />
                <span className="checkmark"></span>
                I agree to the <a href="/terms" target="_blank">Terms and Conditions</a> and <a href="/privacy" target="_blank">Privacy Policy</a>
              </label>
              {errors.acceptTerms && (
                <span className="error-message">{errors.acceptTerms}</span>
              )}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleBack}
              >
                Back
              </button>
              
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <span className="loading-spinner">
                    <span className="dot"></span>
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>
          </div>
        )}

        <div className="form-footer">
          <div className="switch-form">
            <span>Already have an account? </span>
            <button
              type="button"
              className="link-button primary"
              onClick={onSwitchToLogin}
            >
              Sign In
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegisterForm;
